import App from "./App";
import { PersistProvider } from "./data/PersistProvider";
import { RootStateProvider } from "./data/RootStateProvider";
import { RouterProvider } from "./data/RouterProvider";
import { LanguageProvider } from './data/LanguageProvider';
import "./style";
import "./style/iconfont.css";
import FirebaseAnalytics from "./utils/FirebaseAnalytics";

if (process.env.NODE_ENV === "production") {
  console.log = () => { };
  console.error = () => { };
  console.debug = () => { };
}

const DESKTOP_APP_VIEWPORT_WIDTH = 400;
const DESKTOP_APP_BREAKPOINT = 720;
const APP_VW_STEPS = [5, 10, 28, 39, 80, 85, 90];
const DESKTOP_DRAG_SCROLL_MIN_DISTANCE = 5;
const DESKTOP_DRAG_CLICK_GUARD_MS = 350;
const DRAG_SCROLL_IGNORE_SELECTOR = [
  "input",
  "textarea",
  "select",
  "option",
  "[contenteditable='true']",
  ".rv-number-keyboard",
  ".rv-picker",
  ".rv-floating-ball",
  ".app-floating-ball-wrapper"
].join(",");

function getAppViewportWidth() {
  const docWidth = document.documentElement.clientWidth || window.innerWidth || window.screen.width;
  if (docWidth >= DESKTOP_APP_BREAKPOINT) {
    return Math.min(docWidth, DESKTOP_APP_VIEWPORT_WIDTH);
  }
  return docWidth;
}

function getAppViewportHeight() {
  if (window.visualViewport && window.visualViewport.height) {
    return Math.round(window.visualViewport.height);
  }
  return window.innerHeight || document.documentElement.clientHeight || window.screen.height;
}

function updateAppViewportVars() {
  const appWidth = getAppViewportWidth();
  const appHeight = getAppViewportHeight();
  const rootStyle = document.documentElement.style;
  rootStyle.setProperty("--app-viewport-width", `${appWidth}px`);
  rootStyle.setProperty("--app-viewport-height", `${appHeight}px`);
  APP_VW_STEPS.forEach((step) => {
    rootStyle.setProperty(`--app-vw-${step}`, `${(appWidth * step) / 100}px`);
  });
  window.appViewportWidth = appWidth;
  window.appViewportHeight = appHeight;
  return appWidth;
}

function isDesktopAppMode() {
  const docWidth = document.documentElement.clientWidth || window.innerWidth || window.screen.width;
  return docWidth >= DESKTOP_APP_BREAKPOINT;
}

function isScrollableOverflow(value) {
  return value === "auto" || value === "scroll" || value === "overlay";
}

function getScrollableDragTargets(startTarget, root) {
  const targets = [];
  let node = startTarget && startTarget.nodeType === 1 ? startTarget : startTarget.parentElement;

  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const canScrollX = isScrollableOverflow(style.overflowX) && node.scrollWidth > node.clientWidth + 1;
    const canScrollY = isScrollableOverflow(style.overflowY) && node.scrollHeight > node.clientHeight + 1;

    if (canScrollX || canScrollY) {
      targets.push({ element: node, canScrollX, canScrollY });
    }

    if (node === root) {
      break;
    }
    node = node.parentElement;
  }

  return targets;
}

function pickDragTarget(targets, primaryAxis, absX, absY) {
  const primaryKey = primaryAxis === "x" ? "canScrollX" : "canScrollY";
  const primaryTarget = targets.find((target) => target[primaryKey]);

  if (primaryTarget) {
    return { target: primaryTarget, axis: primaryAxis };
  }

  const fallbackAxis = primaryAxis === "x" ? "y" : "x";
  const fallbackDistance = fallbackAxis === "x" ? absX : absY;
  const fallbackKey = fallbackAxis === "x" ? "canScrollX" : "canScrollY";

  if (fallbackDistance >= DESKTOP_DRAG_SCROLL_MIN_DISTANCE) {
    const fallbackTarget = targets.find((target) => target[fallbackKey]);
    if (fallbackTarget) {
      return { target: fallbackTarget, axis: fallbackAxis };
    }
  }

  return null;
}

function installDesktopDragScroll() {
  if (!window.PointerEvent || window.__desktopDragScrollInstalled) {
    return;
  }

  window.__desktopDragScrollInstalled = true;

  let dragState = null;
  let suppressClickUntil = 0;

  function stopDrag() {
    if (dragState && dragState.dragging) {
      suppressClickUntil = Date.now() + DESKTOP_DRAG_CLICK_GUARD_MS;
    }
    document.documentElement.classList.remove("app-drag-scrolling");
    dragState = null;
  }

  window.addEventListener(
    "pointerdown",
    (event) => {
      if (!isDesktopAppMode() || event.pointerType !== "mouse" || event.button !== 0) {
        return;
      }

      const target = event.target;
      if (!target || !target.closest || target.closest(DRAG_SCROLL_IGNORE_SELECTOR)) {
        return;
      }

      const root = target.closest("#app, #preact_root");
      if (!root) {
        return;
      }

      const targets = getScrollableDragTargets(target, root);
      if (!targets.length) {
        return;
      }

      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        targets,
        activeTarget: null,
        axis: null,
        startScrollLeft: 0,
        startScrollTop: 0,
        dragging: false
      };
    },
    true
  );

  window.addEventListener(
    "pointermove",
    (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!dragState.activeTarget) {
        if (Math.max(absX, absY) < DESKTOP_DRAG_SCROLL_MIN_DISTANCE) {
          return;
        }

        const primaryAxis = absX >= absY ? "x" : "y";
        const picked = pickDragTarget(dragState.targets, primaryAxis, absX, absY);
        if (!picked) {
          return;
        }

        dragState.activeTarget = picked.target.element;
        dragState.axis = picked.axis;
        dragState.startScrollLeft = dragState.activeTarget.scrollLeft;
        dragState.startScrollTop = dragState.activeTarget.scrollTop;
        dragState.dragging = true;
        document.documentElement.classList.add("app-drag-scrolling");
      }

      if (dragState.axis === "x") {
        dragState.activeTarget.scrollLeft = dragState.startScrollLeft - deltaX;
      } else {
        dragState.activeTarget.scrollTop = dragState.startScrollTop - deltaY;
      }

      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
      event.preventDefault();
    },
    { capture: true, passive: false }
  );

  window.addEventListener("pointerup", stopDrag, true);
  window.addEventListener("pointercancel", stopDrag, true);
  window.addEventListener("blur", stopDrag, true);

  window.addEventListener(
    "click",
    (event) => {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

function resizeFontsize() {
  let appWidth = updateAppViewportVars();
  let app = document.getElementById("app") || document.getElementById("preact_root");
  let w = app ? app.offsetWidth : appWidth;
  let _w = appWidth;
  console.log("resizeFontsize", w, _w, window.rem);
  if (w > _w && _w < window.rem * 23.8) {
    document.documentElement.style.fontSize = `${(window.rem * _w) / w}px`;
  }
}
if (window) {
  !(function (win) {
    function resize() {
      var domWidth = updateAppViewportVars();
      if (domWidth / v > 720) {
        domWidth = 720 * v;
      }
      win.rem = domWidth / 23.75;
      console.log("resize", domWidth, win.rem);
      domEle.style.fontSize = win.rem + "px";
      try {
        resizeFontsize();
      } catch (e) { }
    }
    var v,
      initial_scale,
      timeCode,
      dom = win.document,
      domEle = dom.documentElement,
      viewport = dom.querySelector('meta[name="viewport"]'),
      flexible = dom.querySelector('meta[name="flexible"]');
    if (viewport) {
      //viewport：<meta name="viewport"content="initial-scale=0.5, minimum-scale=0.5, maximum-scale=0.5,user-scalable=no,minimal-ui"/>
      var o = viewport.getAttribute("content").match(/initial\-scale=(["']?)([\d\.]+)\1?/);
      if (o) {
        initial_scale = parseFloat(o[2]);
        v = parseInt(1 / initial_scale);
      }
    } else {
      if (flexible) {
        var o = flexible.getAttribute("content").match(/initial\-dpr=(["']?)([\d\.]+)\1?/);
        if (o) {
          v = parseFloat(o[2]);
          initial_scale = parseFloat((1 / v).toFixed(2));
        }
      }
    }
    if (!v && !initial_scale) {
      var n = (win.navigator.appVersion.match(/android/gi), win.navigator.appVersion.match(/iphone/gi));
      v = win.devicePixelRatio;
      (v = n ? (v >= 3 ? 3 : v >= 2 ? 2 : 1) : 1), (initial_scale = 1 / v);
    }
    //没有viewport标签的情况下
    if ((domEle.setAttribute("data-dpr", v), !viewport)) {
      if (
        ((viewport = dom.createElement("meta")),
          viewport.setAttribute("name", "viewport"),
          viewport.setAttribute(
            "content",
            "initial-scale=" +
            initial_scale +
            ", maximum-scale=" +
            initial_scale +
            ", minimum-scale=" +
            initial_scale +
            ", user-scalable=no"
          ),
          domEle.firstElementChild)
      ) {
        domEle.firstElementChild.appendChild(viewport);
      } else {
        var m = dom.createElement("div");
        m.appendChild(viewport), dom.write(m.innerHTML);
      }
    }
    win.dpr = v;
    function queueResize(delay) {
      clearTimeout(timeCode), (timeCode = setTimeout(resize, delay));
    }
    win.addEventListener("resize", function () { queueResize(300); }, false);
    win.addEventListener("orientationchange", function () { queueResize(80); }, false);
    if (win.visualViewport) {
      win.visualViewport.addEventListener("resize", function () { queueResize(50); }, false);
      win.visualViewport.addEventListener("scroll", function () { queueResize(50); }, false);
    }
    win.addEventListener(
      "pageshow",
      function (b) {
        b.persisted && (clearTimeout(timeCode), (timeCode = setTimeout(resize, 300)));
      },
      false
    );
    resize();
  })(window);
}

if (window) {
  installDesktopDragScroll();

  if (typeof window.setAttribution != "function") {
    window.setAttribution = function (attributionInfo) {
      if (!localStorage.getItem("attribution") && attributionInfo.id && attributionInfo.name) {
        localStorage.setItem("attribution", attributionInfo.name);
        localStorage.setItem("attribution_id", attributionInfo.id);
        localStorage.setItem("ad_source", attributionInfo.source);
      }
      console.log("setAttribution", attributionInfo);
      return attributionInfo;
    };
  }
  if (typeof window.setAFID != "function") {
    window.setAFID = function (AFID) {
      if (!localStorage.getItem("attribution")) {
        localStorage.setItem("attribution", "af");
        localStorage.setItem("attribution_id", AFID);
      }
      console.log("setAFID", AFID);
      return AFID;
    };
    window.setAppsflyerId = window.setAFID;
  }
  if (typeof window.setAdSource != "function") {
    window.setAdSource = function (adSource) {
      if (!localStorage.getItem("ad_source")) {
        localStorage.setItem("ad_source", adSource);
      }
      console.log("setAdSource", adSource);
      return adSource;
    };
    window.setMediaSource = window.setAdSource;
  }
  if (window.SuWin8Bridge) {
    window.JSBridge = window.SuWin8Bridge;
  }
  if (window.JSInterface) {
    window.JSBridge = window.JSInterface;
    window.JSBridge.SetRid = window.JSInterface.SetUserId;
  }
  if (window.JSBridge && window.JSBridge.SendEvent) {
    window.JSBridge.SendEvent("page_view", JSON.stringify({ page: "home" }));
  }
}
if (process.env.PREACT_APP_ENV_CONFIG.firebaseConfig) {
  FirebaseAnalytics.GetInstance(process.env.PREACT_APP_ENV_CONFIG.firebaseConfig);
}
const Main = () => {
  return (
    <PersistProvider>
      <RootStateProvider>
        <LanguageProvider>
          <RouterProvider>
            <App />
          </RouterProvider>
        </LanguageProvider>
      </RootStateProvider>
    </PersistProvider>
  );
};

export default Main;
